import { Site } from '@pubstack/common/src/site'
import { type FunctionComponent } from 'react'
import Button from '~/components/Button'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Modal } from '~/components/Modal'
import { ConnectToPubstack } from '~/feature-components/connect/ConnectToPubstack'

const TRACKING_LABEL = 'pbs-analytics-script-modal'

/** What stands in for a media id when the tag is being shown for several medias at once. */
export const SITE_ID_PLACEHOLDER = '<siteId>'

type Props = {
  /** The media the tag is for, or — on a batch — whichever one the batch shares its environment with. */
  site: Site
  /** How many medias were just created. Above one, the id in every snippet becomes the placeholder. */
  mediaCount?: number
}

/**
 * The tag, offered the moment a media is created.
 *
 * On a batch there is no such thing as "the tag": each media carries its own id. Rather than showing
 * nothing — which left a bulk import with no hint that a tag was the next step at all — the snippets are
 * printed once with the id replaced by a placeholder, and the reader is told where each real one lives.
 */
export const AnalyticsTagModal: FunctionComponent<Props> = ({ site, mediaCount = 1 }) => {
  const { close } = useGlobalModal()
  const isBatch = mediaCount > 1
  // The placeholder rides in on the media rather than as a prop of its own: every snippet is built from
  // the media it is given, so one substitution covers all of them, whichever environment this is.
  const snippetSite = isBatch ? { ...site, id: SITE_ID_PLACEHOLDER } : site

  return (
    <Modal.Content>
      <Modal.Title>Connect to Pubstack</Modal.Title>
      <Modal.Body>
        <div className="flex w-[700px] flex-col gap-2">
          {isBatch && (
            <span className="font-roboto text-sm leading-[1.375rem] italic text-hurricane">
              Replace <code>{SITE_ID_PLACEHOLDER}</code> with each media&apos;s own id — every one of the {mediaCount} medias you just added carries its tag in its own Connect to Pubstack section.
            </span>
          )}
          <ConnectToPubstack site={snippetSite} trackingPrefix={TRACKING_LABEL} isFolding={false} />
        </div>
      </Modal.Body>
      <Modal.Actions>
        <Button variant="tertiary" onClick={close} trackingLabel={`${TRACKING_LABEL}-close`}>
          Close
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
